import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, LogOut, X } from "lucide-react";
import { supabase } from "../supabaseClient";
import { isDemoWorkspaceOperatorEmail } from "../utils/demoWorkspaceAssignment";
import "./SiteNavigationDrawer.css";

const readTheme = () => {
    try { return localStorage.getItem("spa-theme") || (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"); }
    catch { return "light"; }
  };

export default function SiteNavigationDrawer({ open, onClose, user, onSignIn, email, returnFocusRef }) {
  const ref = useRef(null);
  const location = useLocation();
  const [theme, setTheme] = useState(readTheme);
  useEffect(() => {
    const sync = () => setTheme(readTheme());
    window.addEventListener("spa-theme-change", sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener("spa-theme-change", sync); window.removeEventListener("storage", sync); };
  }, []);
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    try { localStorage.setItem("spa-theme", next); } catch { return; }
    setTheme(next);
    window.dispatchEvent(new Event("spa-theme-change"));
  };
  const [adminProfileId, setAdminProfileId] = useState(null);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState("");
  const signOutErrorRef = useRef(null);
  useEffect(() => { setSignOutError(""); }, [open, user?.id]);
  useEffect(() => {
    if (signOutError) signOutErrorRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [signOutError]);
  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    setSignOutError("");
    try {
      const { error } = await supabase.auth.signOut({ scope: "local" });
      if (error) throw error;
      onClose();
    } catch {
      setSignOutError("Couldn’t sign out. Check your connection and try again.");
    } finally {
      setSigningOut(false);
    }
  };
  useEffect(() => {
    let current = true;
    setAdminProfileId(null);
    if (user?.id) {
      supabase.rpc("is_platform_admin", { p_profile_id: user.id })
        .then(({ data, error }) => {
          if (current && !error && data === true) setAdminProfileId(user.id);
        })
        .catch(() => {});
    }
    return () => { current = false; };
  }, [user?.id]);
  useEffect(() => {
    const dialog = ref.current;
    if (!open) {
      if (dialog?.open) dialog.close();
      return undefined;
    }
    const previous = document.activeElement;
    const returnFocus = returnFocusRef?.current || previous;
    const overflow = document.body.style.overflow;
    const rootOverflow = document.documentElement.style.overflow;
    dialog.showModal();
    const controls = () => [...dialog.querySelectorAll('a[href], button:not(:disabled)')];
    controls()[0]?.focus();
    const containFocus = (event) => {
      if (event.key !== "Tab") return;
      const items = controls();
      const first = items[0], last = items[items.length - 1];
      const outside = !dialog.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || outside)) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || outside)) {
        event.preventDefault(); first?.focus();
      }
    };
    dialog.addEventListener("keydown", containFocus);
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      dialog.removeEventListener("keydown", containFocus);
      dialog.close();
      document.body.style.overflow = overflow;
      document.documentElement.style.overflow = rootOverflow;
      returnFocus?.focus?.();
    };
  }, [open, returnFocusRef]);
  const groups = [
    {
      title: "Sport & fitness",
      items: [
        ["Home", "/"],
        ["Explore the app", "/#explore"],
        ["Watch the app", "/#in-action"],
        ["Communities", "/communities"],
        ["Sport locations", "/sport-locations"],
        ["Sessions", "/sessions"],
        ["Tournaments", "/tournaments/live"],
        ["Leaderboards", "/leaderboard"],
        ["Institute leaderboard", "/instituteleaderboard"],
        ["Live matches", "/livematches"],
      ],
    },
    {
      title: "Our world",
      items: [
        ["Our mission", "/#mission"],
        ["World Wide Steps", "https://worldwidesteps.com/"],
        ["School Step Challenge", "https://worldwidesteps.com/schools"],
        ["Our founder’s story", "https://whereathletesbelong.com/"],
        ["ProAm Store", "https://worldwidesteps.com/shop"],
        ["Sponsors & advertisers", "/#sponsors"],
      ],
    },
    {
      title: "Bring your team",
      items: [
        ["Schools & colleges", "/wellness"],
        ["Clubs & coaching", "/coaching"],
        ["Venues", "/venues"],
        ["List your venue", "/venues/join"],
        ["Support", "/support"],
      ],
    },
    ...(user ? [{
      title: "Account",
      items: [
        ["Institution dashboard", "/profile?tab=institutes"],
        ["Reset password", "/reset-password"],
        ...(isDemoWorkspaceOperatorEmail(email || user.email) ? [["Copy demos", "/outreach?tab=demos"]] : []),
      ],
    }] : []),
    ...(user?.id && adminProfileId === user.id ? [{
      title: "Admin",
      items: [
        ["Admin Dashboard", "/admin"],
        ["Outreach", "/outreach"],
        ["Workspace", "/workspace"],
        ["App Analytics", "/admin/analytics"],
      ],
    }] : []),
  ];
  return (
    <dialog
      ref={ref}
      className="sc-navigation-dialog"
      id="sc-navigation"
      aria-labelledby="sc-navigation-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sc-drawer-card">
        <div className="sc-drawer-top">
          <div className="sc-brand">
            <img
              src="/images/app-showcase/brand-mark.webp"
              alt=""
              width="42"
              height="42"
            />
            <span>Snows ProAm</span>
          </div>
          <button aria-label="Close navigation" onClick={onClose}>
            <X size={24} />
          </button>
          <h2 id="sc-navigation-title">Where Athletes Belong</h2>
        </div>
        <nav aria-label="Explore Snows ProAm" className="sc-drawer-links">
          {groups.map((group) => (
            <div key={group.title}>
              <h3>{group.title}</h3>
              {group.items.map(([title, href]) =>
                href.startsWith("/") ? (
                  <Link key={title} to={href} onClick={onClose} aria-current={href === location.pathname + location.search + location.hash ? "page" : undefined}>
                    {title}
                    <ArrowRight size={15} />
                  </Link>
                ) : (
                  <a
                    key={title}
                    href={href}
                    onClick={onClose}
                    {...(href.startsWith("https:")
                      ? { target: "_blank", rel: "noreferrer" }
                      : {})}
                  >
                    {title}
                    <ArrowRight size={15} />
                  </a>
                ),
              )}
            </div>
          ))}
        </nav>
        <div className="sc-drawer-account">
          {user ? (
            <>
              <Link className="sc-button" to="/profile" onClick={onClose}>
                My profile <ArrowRight size={16} />
              </Link>
              <button type="button" className="sc-text-link sc-sign-out" onClick={handleSignOut} disabled={signingOut} aria-busy={signingOut}>
                {signingOut ? "Signing out…" : "Sign out"} <LogOut size={16} aria-hidden="true" />
              </button>
            </>
          ) : (
            <button
              className="sc-button"
              onClick={() => {
                onClose();
                onSignIn();
              }}
            >
              Sign in <ArrowRight size={16} />
            </button>
          )}
          <Link to="/#download" className="sc-text-link sc-drawer-download" onClick={onClose}>
            Download the app <ArrowRight size={16} />
          </Link>
          <button type="button" className="sc-text-link sc-drawer-theme" onClick={toggleTheme}>
            Use {theme === "dark" ? "light" : "dark"} appearance
          </button>
          {user && signOutError && <p ref={signOutErrorRef} className="sc-sign-out-error" role="alert">{signOutError}</p>}
        </div>
      </div>
    </dialog>
  );
}
